import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    Tabs,
    Tab,
    Grid,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    Pause as PauseIcon,
    PlayArrow as PlayArrowIcon,
    Edit as EditIcon,
    ContentCopy as ContentCopyIcon,
    GetApp as GetAppIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { deleteStrategy, addStrategy } from '../../api/strategyApi';
import { adminOverrideStart, adminOverrideStop } from '../../api/admin_api';

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const StrategyDetailsModal = ({ open, onClose, strategy, onRefresh }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const token = localStorage.getItem('token');

    if (!strategy) return null;

    const getInitials = (name) => {
        const words = name.split('_');
        return words.length >= 2
            ? `${words[0][0]}${words[1][0]}`.toUpperCase()
            : name.substring(0, 2).toUpperCase();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'running': return { bg: '#dbeafe', color: '#1e40af', label: 'Running' };
            case 'stopped': return { bg: '#f3f4f6', color: '#6b7280', label: 'Stopped' };
            case 'error': return { bg: '#fee2e2', color: '#991b1b', label: 'Error' };
            default: return { bg: '#f3f4f6', color: '#6b7280', label: 'Unknown' };
        }
    };

    const statusInfo = getStatusColor(strategy.status);

    // Mock performance data
    const performanceData = {
        currentPnL: 1245.50,
        pnlPercent: 8.3,
        totalPositions: 156,
        buyPositions: 98,
        sellPositions: 58,
        winRate: 68.5,
        wins: 107,
        losses: 49,
        uptime: '2h 45m',
        maxDrawdown: -250.00,
        drawdownPercent: -16.7,
        peakProfit: 1450.00,
        avgTrade: 7.98,
        largestWin: 125.50,
        largestLoss: -48.20
    };

    // Parse real parameters from strategy.parameters object
    const params = strategy.parameters || {};

    // Helper function to get parameter value safely (supports nested paths)
    const getParam = (key, defaultValue = 'N/A') => {
        // Handle nested keys like 'GridManagement.BidirectionalMode'
        const keys = key.split('.');
        let value = params;

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                value = undefined;
                break;
            }
        }

        if (value === undefined || value === null) return defaultValue;
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        return value;
    };

    // Configuration data from real strategy object
    const configData = {
        // Basic Information
        strategyName: strategy.name,
        strategyType: strategy.strategyType || strategy.type || 'N/A',
        owner: strategy.userName || strategy.owner || 'N/A',
        accountNumber: strategy.accountNumber || 'N/A',
        magicId: getParam('MagicId', getParam('magicId', 'N/A')),
        created: strategy.createdAt || 'N/A',
        lastModified: strategy.lastModifiedAt || strategy.updatedAt || 'N/A',

        // Symbols
        futuresSymbol: getParam('FuturesSymbol', strategy.futuresSymbol || (strategy.symbols && strategy.symbols[0]) || 'N/A'),
        spotSymbol: getParam('SpotSymbol', strategy.spotSymbol || (strategy.symbols && strategy.symbols[1]) || 'N/A'),

        // Core Settings
        enabled: getParam('Enabled', strategy.isEnabled),
        mode: getParam('Mode', 'N/A'),

        // Grid Management
        bidirectionalMode: getParam('GridManagement.BidirectionalMode', 'N/A'),
        baseLotSize: getParam('GridManagement.BaseLotSize', getParam('baseLotSize', 'N/A')),
        buyGrid: getParam('GridManagement.BuyGrid', getParam('buyGrid', 'N/A')),
        sellGrid: getParam('GridManagement.SellGrid', getParam('sellGrid', 'N/A')),
        buyLegTP: getParam('GridManagement.BuyLegTP', getParam('buyLegTP', 'N/A')),
        sellLegTP: getParam('GridManagement.SellLegTP', getParam('sellLegTP', 'N/A')),
        accumulativeTP: getParam('GridManagement.AccumulativeTP', getParam('accumulativeTP', 'N/A')),
        accumulativeSL: getParam('GridManagement.AccumulativeSL', getParam('accumulativeSL', 'N/A')),
        maxTotalLotBothDirections: getParam('GridManagement.MaxTotalLotBothDirections', 'N/A'),
        useBenchmarkMode: getParam('GridManagement.UseBenchmarkMode', 'N/A'),
        benchmarkThresholdBuy: getParam('GridManagement.BenchmarkThresholdBuy', 'N/A'),
        benchmarkThresholdSell: getParam('GridManagement.BenchmarkThresholdSell', 'N/A'),

        // Risk Management
        riskEnabled: getParam('RiskManagement.Enabled', 'N/A'),
        maxDrawdown: getParam('RiskManagement.MaxDrawdown', getParam('maxDrawdown', 'N/A')),
        maxPositions: getParam('RiskManagement.MaxPositions', getParam('maxPositions', 'N/A')),

        // Spread Validation
        spreadValidationEnabled: getParam('SpreadValidation.Enabled', 'N/A'),
        minAcceptableSpread: getParam('SpreadValidation.MinAcceptableSpread', 'N/A'),
        maxAcceptableSpread: getParam('SpreadValidation.MaxAcceptableSpread', 'N/A')
    };

    // Mock positions data
    const positions = [
        { id: 1, symbol: 'XAUUSD', type: 'Buy', lot: 0.01, openPrice: 1850.50, currentPrice: 1852.30, pnl: 18.00, status: 'Open' },
        { id: 2, symbol: 'XAGUSD', type: 'Sell', lot: 0.01, openPrice: 23.45, currentPrice: 23.40, pnl: 5.00, status: 'Open' },
        { id: 3, symbol: 'XAUUSD', type: 'Buy', lot: 0.01, openPrice: 1849.00, currentPrice: 1852.30, pnl: 33.00, status: 'Open' }
    ];

    // Mock logs data
    const logs = [
        { time: '09:15:22', level: 'INFO', message: 'Strategy started successfully' },
        { time: '09:16:01', level: 'INFO', message: 'New position opened: XAUUSD Buy @ 1850.50' },
        { time: '09:17:45', level: 'INFO', message: 'New position opened: XAGUSD Sell @ 23.45' },
        { time: '09:20:12', level: 'SUCCESS', message: 'Position closed: XAUUSD Buy +$12.50' },
        { time: '09:22:30', level: 'WARNING', message: 'Spread widening detected: 18.2 pips' }
    ];

    // Handler Functions

    // 1. Stop/Start Strategy - Using Admin Override Endpoints
    const handleStopStrategy = async () => {
        try {
            setLoading(true);
            const result = await adminOverrideStop(strategy.id);
            if (result.success) {
                toast.success('Strategy stopped successfully (Admin Override)');
                if (onRefresh) onRefresh();
            } else {
                toast.error(result.error || 'Failed to stop strategy');
            }
        } catch (error) {
            console.error('Error stopping strategy:', error);
            toast.error(error.message || 'Failed to stop strategy');
        } finally {
            setLoading(false);
        }
    };

    const handleStartStrategy = async () => {
        try {
            setLoading(true);
            const result = await adminOverrideStart(strategy.id);
            if (result.success) {
                toast.success('Strategy started successfully (Admin Override)');
                if (onRefresh) onRefresh();
            } else {
                toast.error(result.error || 'Failed to start strategy');
            }
        } catch (error) {
            console.error('Error starting strategy:', error);
            toast.error(error.message || 'Failed to start strategy');
        } finally {
            setLoading(false);
        }
    };

    // 2. Clone Strategy
    const handleClone = async () => {
        try {
            setLoading(true);

            // Generate new Magic ID (6 digits)
            const newMagicId = Math.floor(100000 + Math.random() * 900000);

            // Clone strategy data
            const clonedStrategy = {
                name: `${strategy.name} (Copy)`,
                description: strategy.description || `Cloned from ${strategy.name}`,
                accountNumber: strategy.accountNumber,
                strategyType: strategy.strategyType,
                isEnabled: false, // Start disabled
                isMultiSymbol: strategy.isMultiSymbol !== undefined ? strategy.isMultiSymbol : true,
                symbols: strategy.symbols || [strategy.futuresSymbol, strategy.spotSymbol].filter(Boolean),
                parameters: strategy.parameters || {}
            };

            const result = await addStrategy(token, clonedStrategy);

            if (result.success || result.data) {
                toast.success('Strategy cloned successfully');
                if (onRefresh) onRefresh();
                onClose();
            } else {
                toast.error(result.message || 'Failed to clone strategy');
            }
        } catch (error) {
            console.error('Error cloning strategy:', error);
            toast.error(error.message || 'Failed to clone strategy');
        } finally {
            setLoading(false);
        }
    };

    // 3. Export Strategy
    const handleExport = () => {
        try {
            const exportData = {
                name: strategy.name,
                description: strategy.description,
                strategyType: strategy.strategyType,
                accountNumber: strategy.accountNumber,
                isMultiSymbol: strategy.isMultiSymbol,
                symbols: strategy.symbols || [strategy.futuresSymbol, strategy.spotSymbol].filter(Boolean),
                parameters: strategy.parameters || {},
                exportedAt: new Date().toISOString(),
                exportedBy: 'Admin'
            };

            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${strategy.name.replace(/\s+/g, '_')}_config.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast.success('Strategy exported successfully');
        } catch (error) {
            console.error('Error exporting strategy:', error);
            toast.error('Failed to export strategy');
        }
    };

    // 4. Delete Strategy
    const handleDelete = async () => {
        const confirmed = window.confirm(
            `Are you sure you want to delete the strategy "${strategy.name}"?\n\nThis action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            setLoading(true);
            const result = await deleteStrategy(token, strategy.id);

            if (result.success || result.data) {
                toast.success('Strategy deleted successfully');
                if (onRefresh) onRefresh();
                onClose();
            } else {
                toast.error(result.message || 'Failed to delete strategy');
            }
        } catch (error) {
            console.error('Error deleting strategy:', error);
            toast.error(error.message || 'Failed to delete strategy');
        } finally {
            setLoading(false);
        }
    };

    // Determine if strategy is running
    const isRunning = strategy.status === 'running' || strategy.status === 'active';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
                    background: '#2d2d2d',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                padding: 3,
                background: '#2d2d2d'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                        {strategy.name} Strategy Details
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 1)' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                padding: 0,
                background: '#2d2d2d'
            }}>
                {/* Strategy Header Info */}
                <Box sx={{ padding: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#2d2d2d' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                        <Avatar sx={{
                            width: 60,
                            height: 60,
                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                            fontSize: '1.25rem',
                            fontWeight: 700
                        }}>
                            {getInitials(strategy.name)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 0.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                    {strategy.name}
                                </Typography>
                                <Chip
                                    label={statusInfo.label}
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        background: statusInfo.bg,
                                        color: statusInfo.color,
                                        borderRadius: '6px'
                                    }}
                                />
                                <Chip label={strategy.strategyType || strategy.type || 'N/A'} size="small" sx={{ fontWeight: 600, background: 'rgba(124, 58, 237, 0.2)', color: '#c4b5fd', borderRadius: '6px' }} />
                                {strategy.symbols ? strategy.symbols.map((symbol, idx) => (
                                    <Chip key={idx} label={symbol} size="small" variant="outlined" sx={{ borderRadius: '6px', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'rgba(255, 255, 255, 0.7)' }} />
                                )) : (
                                    <>
                                        {strategy.futuresSymbol && strategy.futuresSymbol !== 'N/A' && (
                                            <Chip label={strategy.futuresSymbol} size="small" variant="outlined" sx={{ borderRadius: '6px', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'rgba(255, 255, 255, 0.7)' }} />
                                        )}
                                        {strategy.spotSymbol && strategy.spotSymbol !== 'N/A' && (
                                            <Chip label={strategy.spotSymbol} size="small" variant="outlined" sx={{ borderRadius: '6px', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'rgba(255, 255, 255, 0.7)' }} />
                                        )}
                                    </>
                                )}
                            </Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                Owner: {strategy.userName || strategy.owner || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                                Created: Jan 15, 2025 14:30 • Last Modified: Jan 16, 2025 09:15
                            </Typography>
                        </Box>
                    </Box>

                    {/* Stats Cards */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ background: '#3a3a3a', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                                <CardContent sx={{ padding: '16px !important' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginBottom: 0.5 }}>
                                        Current P&L
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: performanceData.pnlPercent >= 0 ? '#22c55e' : '#ef4444' }}>
                                        ${performanceData.currentPnL.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: performanceData.pnlPercent >= 0 ? '#22c55e' : '#ef4444' }}>
                                        {performanceData.pnlPercent >= 0 ? '↑' : '↓'} {Math.abs(performanceData.pnlPercent)}%
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ background: '#3a3a3a', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                                <CardContent sx={{ padding: '16px !important' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginBottom: 0.5 }}>
                                        Total Positions
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        {performanceData.totalPositions}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        {performanceData.buyPositions} Buy / {performanceData.sellPositions} Sell
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ background: '#3a3a3a', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                                <CardContent sx={{ padding: '16px !important' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginBottom: 0.5 }}>
                                        Win Rate
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        {performanceData.winRate}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                                        {performanceData.wins} Win / {performanceData.losses} Loss
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ background: '#3a3a3a', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                                <CardContent sx={{ padding: '16px !important' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', marginBottom: 0.5 }}>
                                        Uptime
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        {performanceData.uptime}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#22c55e' }}>
                                        Active
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', background: '#2d2d2d' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        sx={{
                            paddingX: 3,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                minHeight: 56,
                                color: 'rgba(255, 255, 255, 0.6)'
                            },
                            '& .MuiTab-root.Mui-selected': {
                                color: '#7c3aed'
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#7c3aed',
                                height: 3
                            }
                        }}
                    >
                        <Tab label="Overview" />
                        <Tab label="Configuration" />
                        <Tab label="Positions" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <Box sx={{ paddingX: 3, background: '#2d2d2d' }}>
                    {/* Overview Tab */}
                    <TabPanel value={activeTab} index={0}>
                        <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            Key Metrics
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ padding: 2, background: '#3a3a3a', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: 0.5 }}>Max Drawdown</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                        ${Math.abs(performanceData.maxDrawdown).toLocaleString()} ({performanceData.drawdownPercent}%)
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ padding: 2, background: '#3a3a3a', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: 0.5 }}>Peak Profit</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#22c55e' }}>
                                        ${performanceData.peakProfit.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ padding: 2, background: '#3a3a3a', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: 0.5 }}>Average Trade</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        ${performanceData.avgTrade}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ padding: 2, background: '#3a3a3a', borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: 0.5 }}>Largest Win / Loss</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                        ${performanceData.largestWin} / ${Math.abs(performanceData.largestLoss)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Configuration Tab */}
                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                                Strategy Configuration
                            </Typography>
                        </Box>

                        {/* Multi-Section Grid Layout */}
                        <Grid container spacing={3}>
                            {/* BASIC INFORMATION */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'rgba(124, 58, 237, 0.05)',
                                    border: '1px solid rgba(124, 58, 237, 0.2)',
                                    borderLeft: '4px solid #7c3aed',
                                    borderRadius: '8px',
                                    padding: 2.5,
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" sx={{ color: '#7c3aed', marginBottom: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                                        BASIC INFORMATION
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Strategy Name</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.strategyName}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Owner</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.owner}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Account</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.accountNumber}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Strategy Type</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.strategyType}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Magic ID</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.magicId}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* CORE SETTINGS */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'rgba(124, 58, 237, 0.05)',
                                    border: '1px solid rgba(124, 58, 237, 0.2)',
                                    borderLeft: '4px solid #7c3aed',
                                    borderRadius: '8px',
                                    padding: 2.5,
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" sx={{ color: '#7c3aed', marginBottom: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                                        CORE SETTINGS
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Enabled</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.enabled}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Mode</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.mode}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Futures Symbol</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.futuresSymbol}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(124, 58, 237, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Spot Symbol</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.spotSymbol}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* GRID MANAGEMENT */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'rgba(34, 197, 94, 0.05)',
                                    border: '1px solid rgba(34, 197, 94, 0.2)',
                                    borderLeft: '4px solid #22c55e',
                                    borderRadius: '8px',
                                    padding: 2.5,
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" sx={{ color: '#22c55e', marginBottom: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                                        GRID MANAGEMENT
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Bidirectional Mode</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.bidirectionalMode}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Base Lot Size</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.baseLotSize}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Buy Grid</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.buyGrid}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Sell Grid</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.sellGrid}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Buy Leg TP</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.buyLegTP}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Sell Leg TP</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.sellLegTP}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Accumulative TP</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.accumulativeTP}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Accumulative SL</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.accumulativeSL}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Max Total Lot (Both)</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.maxTotalLotBothDirections}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Benchmark Mode</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.useBenchmarkMode}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Benchmark Buy</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.benchmarkThresholdBuy}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(34, 197, 94, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Benchmark Sell</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.benchmarkThresholdSell}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* RISK MANAGEMENT & SPREAD VALIDATION */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{
                                    background: 'rgba(239, 68, 68, 0.05)',
                                    border: '1px solid rgba(239, 68, 68, 0.2)',
                                    borderLeft: '4px solid #ef4444',
                                    borderRadius: '8px',
                                    padding: 2.5,
                                    height: '100%'
                                }}>
                                    <Typography variant="h6" sx={{ color: '#ef4444', marginBottom: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                                        RISK MANAGEMENT
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Risk Enabled</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.riskEnabled}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(239, 68, 68, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Max Positions</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.maxPositions}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(239, 68, 68, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Max Drawdown</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.maxDrawdown}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Typography variant="h6" sx={{ color: '#f59e0b', marginTop: 3, marginBottom: 2, fontWeight: 600, fontSize: '0.9rem' }}>
                                        SPREAD VALIDATION
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Validation Enabled</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.spreadValidationEnabled}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(245, 158, 11, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Min Acceptable Spread</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.minAcceptableSpread}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}><Divider sx={{ borderColor: 'rgba(245, 158, 11, 0.15)' }} /></Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>Max Acceptable Spread</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', color: 'rgba(255, 255, 255, 0.95)' }}>
                                                {configData.maxAcceptableSpread}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Positions Tab */}
                    <TabPanel value={activeTab} index={2}>
                        <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            Active Positions ({positions.length})
                        </Typography>
                        <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px', background: '#3a3a3a' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ background: '#3a3a3a' }}>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Lot</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Open Price</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Current Price</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>P&L</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {positions.map((position) => (
                                        <TableRow key={position.id} sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.05)' } }}>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>#{position.id}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{position.symbol}</TableCell>
                                            <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                                                <Chip
                                                    label={position.type}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        background: position.type === 'Buy' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                                        color: position.type === 'Buy' ? '#22c55e' : '#ef4444'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{position.lot}</TableCell>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{position.openPrice}</TableCell>
                                            <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.1)' }}>{position.currentPrice}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: position.pnl >= 0 ? '#22c55e' : '#ef4444', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                                                ${position.pnl.toFixed(2)}
                                            </TableCell>
                                            <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                                                <Chip label={position.status} size="small" sx={{ background: 'rgba(124, 58, 237, 0.2)', color: '#c4b5fd', fontWeight: 600 }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                padding: 3,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                background: '#2d2d2d',
                gap: 1.5
            }}>
                {/* Stop/Start Button - Dynamic based on status */}
                {isRunning ? (
                    <Button
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={16} sx={{ color: '#ef4444' }} /> : <PauseIcon />}
                        onClick={handleStopStrategy}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            color: '#ef4444',
                            borderColor: 'rgba(239, 68, 68, 0.5)',
                            '&:hover': {
                                borderColor: '#ef4444',
                                background: 'rgba(239, 68, 68, 0.1)'
                            },
                            '&:disabled': {
                                borderColor: 'rgba(239, 68, 68, 0.3)',
                                color: 'rgba(239, 68, 68, 0.5)'
                            }
                        }}
                    >
                        {loading ? 'Stopping...' : 'Stop'}
                    </Button>
                ) : (
                    <Button
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={16} sx={{ color: '#22c55e' }} /> : <PlayArrowIcon />}
                        onClick={handleStartStrategy}
                        disabled={loading}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            color: '#22c55e',
                            borderColor: 'rgba(34, 197, 94, 0.5)',
                            '&:hover': {
                                borderColor: '#22c55e',
                                background: 'rgba(34, 197, 94, 0.1)'
                            },
                            '&:disabled': {
                                borderColor: 'rgba(34, 197, 94, 0.3)',
                                color: 'rgba(34, 197, 94, 0.5)'
                            }
                        }}
                    >
                        {loading ? 'Starting...' : 'Start'}
                    </Button>
                )}

                {/* Edit Config Button - TODO: Will integrate with CreateStrategyWizard */}
                <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        color: '#7c3aed',
                        borderColor: 'rgba(124, 58, 237, 0.5)',
                        '&:hover': {
                            borderColor: '#7c3aed',
                            background: 'rgba(124, 58, 237, 0.1)'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(124, 58, 237, 0.3)',
                            color: 'rgba(124, 58, 237, 0.5)'
                        }
                    }}
                >
                    Edit Config
                </Button>

                {/* Clone Button */}
                <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} /> : <ContentCopyIcon />}
                    onClick={handleClone}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            background: 'rgba(255, 255, 255, 0.05)'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'rgba(255, 255, 255, 0.4)'
                        }
                    }}
                >
                    {loading ? 'Cloning...' : 'Clone'}
                </Button>

                {/* Export Button */}
                <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={handleExport}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        color: 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            background: 'rgba(255, 255, 255, 0.05)'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'rgba(255, 255, 255, 0.4)'
                        }
                    }}
                >
                    Export
                </Button>

                {/* Delete Button */}
                <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: '#ef4444' }} /> : <DeleteIcon />}
                    onClick={handleDelete}
                    disabled={loading}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        color: '#ef4444',
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        '&:hover': {
                            borderColor: '#ef4444',
                            background: 'rgba(239, 68, 68, 0.1)'
                        },
                        '&:disabled': {
                            borderColor: 'rgba(239, 68, 68, 0.3)',
                            color: 'rgba(239, 68, 68, 0.5)'
                        }
                    }}
                >
                    {loading ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StrategyDetailsModal;
